// What the Organization pages do, without what they look like: the add/edit
// form, the delete confirmation and the writes for a region, a dealer, a
// workshop and a person. Super Admin's pages and the brand's own pages both
// render on top of these, so the two cannot drift apart.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSaveTenantOrganization } from "../../lib/api";
import {
  BLANK_PERSON,
  dealerFormErrors,
  deletionImpact,
  personFormErrors,
  personFormMode,
  personFormValues,
  personPayload,
  regionFormErrors,
  workshopFormErrors,
} from "../../lib/organization";

const detail = (e, fallback) => e?.response?.data?.detail || fallback;

/** Shared by all three: ask first (with what it takes along), then delete.
 *  `confirmDelete` resolves true once the thing is gone. */
function useDelete(save, kind, ix) {
  const [deleting, setDeleting] = useState(null);
  const askDelete = (id) => setDeleting({ id, ...deletionImpact(kind, id, ix) });
  const confirmDelete = async () => {
    try {
      await save.mutateAsync({ kind, id: deleting.id, remove: true });
      toast.success(`${deleting.name} deleted.`);
      setDeleting(null);
      return true;
    } catch (e) {
      toast.error(detail(e, "Could not delete that."));
      return false;
    }
  };
  return { deleting, setDeleting, askDelete, confirmDelete };
}

export function useRegionEditor(tenantId, ix) {
  const save = useSaveTenantOrganization(tenantId);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [errors, setErrors] = useState({});

  const openAdd = () => {
    setErrors({});
    setEditing(null);
    setName("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setErrors({});
    setEditing(row);
    setName(row.name);
    setOpen(true);
  };

  const submit = async () => {
    const found = regionFormErrors(name, ix, editing?.id);
    if (Object.keys(found).length) return setErrors(found);
    try {
      await save.mutateAsync({
        kind: "regions",
        ...(editing ? { id: editing.id, put: true } : {}),
        name: name.trim(),
      });
      toast.success(editing ? "Saved." : "Region added.");
      setOpen(false);
    } catch (e) {
      toast.error(detail(e, "Could not save that."));
    }
  };

  return {
    open,
    setOpen,
    editing,
    name,
    setName,
    errors,
    openAdd,
    openEdit,
    submit,
    pending: save.isPending,
    ...useDelete(save, "regions", ix),
  };
}

const BLANK_DEALER = { name: "", dealer_code: "", region_ids: [] };

export function useDealerEditor(tenantId, ix) {
  const save = useSaveTenantOrganization(tenantId);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK_DEALER);
  const [errors, setErrors] = useState({});

  const openAdd = () => {
    setErrors({});
    setEditing(null);
    setForm(BLANK_DEALER);
    setOpen(true);
  };

  // `row` is a dealerRows() row.
  const openEdit = (row) => {
    setErrors({});
    setEditing(row);
    setForm({ name: row.name, dealer_code: row.code, region_ids: row.regionIds });
    setOpen(true);
  };

  const toggleRegion = (id) =>
    setForm((f) => ({
      ...f,
      region_ids: f.region_ids.includes(id)
        ? f.region_ids.filter((x) => x !== id)
        : [...f.region_ids, id],
    }));

  const submit = async () => {
    const found = dealerFormErrors(form, ix, editing?.id);
    if (Object.keys(found).length) return setErrors(found);
    try {
      await save.mutateAsync({
        kind: "dealers",
        ...(editing ? { id: editing.id, put: true } : {}),
        name: form.name.trim(),
        dealer_code: form.dealer_code.trim(),
        region_ids: form.region_ids,
      });
      toast.success(editing ? "Saved." : "Dealer added.");
      setOpen(false);
    } catch (e) {
      toast.error(detail(e, "Could not save that."));
    }
  };

  return {
    open,
    setOpen,
    editing,
    form,
    setForm,
    errors,
    openAdd,
    openEdit,
    toggleRegion,
    submit,
    pending: save.isPending,
    ...useDelete(save, "dealers", ix),
  };
}

const BLANK_WORKSHOP = { workshop_location: "", dealer_id: "" };

export function useWorkshopEditor(tenantId, ix) {
  const save = useSaveTenantOrganization(tenantId);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK_WORKSHOP);
  const [errors, setErrors] = useState({});

  // Starts on the dealer it was opened from, else on the first one.
  const openAdd = (dealerId = ix.dealers[0]?.id) => {
    setErrors({});
    setForm({ ...BLANK_WORKSHOP, dealer_id: dealerId != null ? String(dealerId) : "" });
    setAdding(true);
  };

  const submitAdd = async () => {
    const found = workshopFormErrors(form, ix);
    if (Object.keys(found).length) return setErrors(found);
    try {
      await save.mutateAsync({
        kind: "workshops",
        workshop_location: form.workshop_location.trim(),
        dealer_id: Number(form.dealer_id),
      });
      toast.success("Workshop added.");
      setAdding(false);
    } catch (e) {
      toast.error(detail(e, "Could not save that."));
    }
  };

  // `row` is a workshopRows() row.
  const toggleStatus = async (row) => {
    const next = row.status === "paused" ? "active" : "paused";
    try {
      await save.mutateAsync({ kind: "workshops", id: row.id, patch: true, status: next });
      toast.success(next === "paused" ? `${row.location} paused.` : `${row.location} is live again.`);
    } catch (e) {
      toast.error(detail(e, "Could not change that."));
    }
  };

  return {
    adding,
    setAdding,
    form,
    setForm,
    errors,
    openAdd,
    submitAdd,
    toggleStatus,
    pending: save.isPending,
    ...useDelete(save, "workshops", ix),
  };
}

/** Removing a person: a login, or a contact with none. */
export function usePersonRemoval(tenantId, ix) {
  const save = useSaveTenantOrganization(tenantId);
  const [deleting, setDeleting] = useState(null);

  const askRemove = (id, hasLogin) => setDeleting({ id, hasLogin, ...deletionImpact("people", id, ix) });

  const confirmRemove = async () => {
    try {
      await save.mutateAsync({ kind: "people", id: deleting.id, remove: true });
      toast.success(`${deleting.name} removed.`);
      setDeleting(null);
      return true;
    } catch (e) {
      toast.error(detail(e, "Could not remove them."));
      return false;
    }
  };

  return { deleting, setDeleting, askRemove, confirmRemove, pending: save.isPending };
}

/**
 * Adding or editing one person.
 *
 * @param person      the person being edited, or null to add
 * @param atWorkshop  pins a new person to this workshop — used when adding
 *                    from inside one workshop's team
 */
export function usePersonForm({ open, onOpenChange, tenantId, ix, person, atWorkshop }) {
  const save = useSaveTenantOrganization(tenantId);
  const [form, setForm] = useState(BLANK_PERSON);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(personFormValues(person, ix, atWorkshop));
  }, [open, person, atWorkshop, ix]);

  const mode = personFormMode({ form, person, atWorkshop, ix });

  const toggleId = (id) =>
    setForm((f) => ({
      ...f,
      scope_ids: f.scope_ids.includes(id) ? f.scope_ids.filter((x) => x !== id) : [...f.scope_ids, id],
    }));

  // A new role asks a new scope question, so the old answer is dropped.
  const setRole = (code) =>
    setForm({ ...form, role: code, workshop_location: atWorkshop || "", scope_id: "", scope_ids: [] });

  const submit = async () => {
    const found = personFormErrors(form, mode, atWorkshop);
    if (Object.keys(found).length) return setErrors(found);
    const values = personPayload(form, mode.question, atWorkshop);
    try {
      await save.mutateAsync({
        kind: "people",
        // The prefixed id, not user_id: it says which table the row came
        // from, and a contact row has no user_id at all.
        ...(person ? { id: person.id, put: true } : {}),
        ...values,
      });
      toast.success(
        person ? "Saved." : values.email ? "Login added. They set their own password at sign-up." : "Added.",
      );
      onOpenChange(false);
    } catch (e) {
      toast.error(detail(e, "Could not save that."));
    }
  };

  return { form, setForm, errors, toggleId, setRole, submit, pending: save.isPending, ...mode };
}
